import React, { useState } from 'react';
import { getApiUrl } from '../config/api';
import { trackEvent } from '../utils/matomoEvents';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (optional but must be valid if provided)
    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError('');
      
      // Track form submission attempt with Matomo
      trackEvent('Contact', 'FormSubmit', 'ContactForm');
      
      try {
        // Prepare email data for the API - match the exact format that works in PowerShell
        const emailData = {
          to: "gatorskinfrenchies@gmail.com",
          subject: `Contact Form Submission from ${formData.name}`,
          text: `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone || 'Not provided'}\n\nMessage: ${formData.message}`,
          html: `<p>This is a <strong>contact form submission</strong> from ${formData.name}.</p>
            <h3>New Contact Form Submission</h3>
            <p><strong>Name:</strong> ${formData.name}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Phone:</strong> ${formData.phone || 'Not provided'}</p>
            <p><strong>Message:</strong></p>
            <p>${formData.message.replace(/\n/g, '<br>')}</p>
          `
        };
        
        // Ensure we're not sending any unexpected properties
        // This matches exactly the format that works in PowerShell
        
        // Debug: Log the API URL and request data
        const apiUrl = getApiUrl('/api/send-email');
        console.log('Sending email to API URL:', apiUrl);
        console.log('Email data being sent:', emailData);
        
        // Send data to the API
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(emailData)
        });
        
        // Debug: Log the response status
        console.log('API response status:', response.status);
        console.log('Response headers:', [...response.headers.entries()]);
        
        // Try to get response body regardless of status
        const responseText = await response.text();
        console.log('Response body:', responseText);
        
        // Now check if response was ok
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}. Body: ${responseText}`);
        }
        
        // Handle successful submission
        console.log('Form submitted successfully');
        setSubmitted(true);
        
        // Track successful form submission with Matomo
        trackEvent('Contact', 'FormSubmitSuccess', 'ContactForm');
        
        // Reset form after submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitError('There was a problem sending your message. Please try again later.');
        
        // Track form submission error with Matomo
        trackEvent('Contact', 'FormSubmitError', 'ContactForm', 0);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <section className="contact" id="contact">
      <h2>Contact Us</h2>
      
      <div className="contact-info">
        <div className="contact-details">
          <h3>Get in Touch</h3>
          <p>Interested in our French Bulldogs? We'd love to hear from you!</p>
          <div className="contact-method">
            <strong>Phone:</strong> (555) 123-4567
          </div>
          <div className="contact-method">
            <strong>Email:</strong> info@vibegator.com
          </div>
        </div>
        
        {submitted ? (
          <div className="success-message">
            <h3>Thank You!</h3>
            <p>Your message has been sent successfully. We'll get back to you soon!</p>
            <button 
              className="contact-button"
              onClick={() => setSubmitted(false)}
            >
              Send Another Message
            </button>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={errors.phone ? 'error' : ''}
                placeholder="(Optional)"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={errors.message ? 'error' : ''}
                rows="5"
              ></textarea>
              {errors.message && <span className="error-message">{errors.message}</span>}
            </div>
            
            {submitError && <div className="error-message submit-error">{submitError}</div>}
            <button 
              type="submit" 
              className="contact-button" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
      
      <style jsx>{`
        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 2rem;
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          background-color: var(--surface);
          border-radius: 10px;
          box-shadow: 0 4px 8px var(--shadow);
        }
        
        .contact-details {
          text-align: left;
        }
        
        .contact-method {
          margin: 0.5rem 0;
        }
        
        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          text-align: left;
        }
        
        label {
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        
        input, textarea {
          padding: 0.75rem;
          border: 1px solid var(--border);
          border-radius: 5px;
          font-size: 1rem;
        }
        
        input.error, textarea.error {
          border-color: var(--danger);
        }
        
        .error-message {
          color: var(--danger);
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }
        
        .submit-error {
          padding: 0.5rem;
          background-color: rgba(231, 76, 60, 0.1);
          border-radius: 4px;
          text-align: center;
          margin-bottom: 0.5rem;
        }
        
        .contact-button:disabled {
          background-color: var(--border);
          cursor: not-allowed;
          opacity: 0.7;
        }
        
        .success-message {
          text-align: center;
          padding: 2rem;
        }
        
        @media (min-width: 768px) {
          .contact-info {
            flex-direction: row;
            align-items: flex-start;
          }
          
          .contact-details, .contact-form, .success-message {
            flex: 1;
          }
        }
      `}</style>
    </section>
  );
}